import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Download, 
  Filter, 
  Search, 
  Eye, 
  Phone, 
  Mail, 
  Calendar,
  TrendingUp,
  UserCheck,
  UserX,
  BookOpen,
  BarChart3,
  Trash2,
  Edit,
  MessageSquare,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { 
  getAllLeads, 
  getLeadsStats, 
  exportLeadsToCSV, 
  deleteLead,
  updateLeadStatus,
  updateMultipleLeadsStatus,
  addLeadNote,
  CourseLeadData 
} from '../../utils/leads';

const LeadsManager = () => {
  const { user, selectedClient } = useAuth();
  const activeClientId = selectedClient?.clientId || user?.current_client_id || null;
  const [leads, setLeads] = useState<(CourseLeadData & { id: string })[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<(CourseLeadData & { id: string })[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<(CourseLeadData & { id: string }) | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);

  useEffect(() => {
    loadLeads();
  }, [activeClientId]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter, courseFilter]);

  const loadLeads = async () => {
    try {
      setIsLoading(true);
      if (!activeClientId) {
        setLeads([]);
        setFilteredLeads([]);
        setStats({
          total: 0,
          new: 0,
          contacted: 0,
          interested: 0,
          enrolled: 0,
          notInterested: 0,
          resolved: 0,
          courseStats: {},
          professionStats: {},
          conversionRate: 0
        });
        return;
      }

      const [leadsData, statsData] = await Promise.all([
        getAllLeads(activeClientId),
        getLeadsStats(activeClientId)
      ]);
      setLeads(leadsData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      toast.error('Erro ao carregar dados dos leads');
    } finally {
      setIsLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = leads;

    // Filtro por texto
    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm) ||
        lead.profession.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Filtro por curso
    if (courseFilter !== 'all') {
      filtered = filtered.filter(lead => lead.selectedCourse === courseFilter);
    }

    setFilteredLeads(filtered);
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      if (!activeClientId) {
        toast.error('Tenant não selecionado');
        return;
      }
      await deleteLead(leadId, activeClientId);
      setLeads(leads.filter(lead => lead.id !== leadId));
      toast.success('Lead excluído com sucesso!');
      
      // Recarregar estatísticas
      const newStats = await getLeadsStats(activeClientId);
      setStats(newStats);
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      toast.error('Erro ao excluir lead');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const handleUpdateStatus = async (leadId: string, newStatus: CourseLeadData['status']) => {
    try {
      setIsUpdating(true);
      if (!activeClientId) {
        toast.error('Tenant não selecionado');
        return;
      }
      await updateLeadStatus(leadId, newStatus, undefined, activeClientId);
      await loadLeads(); // Recarregar dados
      toast.success('Status atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    } finally {
      setIsUpdating(false);
      setShowStatusModal(null);
    }
  };

  const handleAddNote = async (leadId: string) => {
    if (!newNote.trim()) {
      toast.error('Digite uma nota antes de salvar');
      return;
    }

    try {
      setIsUpdating(true);
      if (!activeClientId) {
        toast.error('Tenant não selecionado');
        return;
      }
      await addLeadNote(leadId, newNote.trim(), activeClientId);
      await loadLeads(); // Recarregar dados
      toast.success('Nota adicionada com sucesso!');
      setNewNote('');
    } catch (error) {
      console.error('Erro ao adicionar nota:', error);
      toast.error('Erro ao adicionar nota');
    } finally {
      setIsUpdating(false);
      setShowNotesModal(null);
    }
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(lead => lead.id));
    }
  };

  const handleBulkStatusUpdate = async (newStatus: CourseLeadData['status']) => {
    if (selectedLeads.length === 0) {
      toast.error('Selecione pelo menos um lead');
      return;
    }

    try {
      setIsUpdating(true);
      if (!activeClientId) {
        toast.error('Tenant não selecionado');
        return;
      }
      await updateMultipleLeadsStatus(selectedLeads, newStatus, undefined, activeClientId);
      await loadLeads();
      setSelectedLeads([]);
      setShowBulkStatusModal(false);
      toast.success(`${selectedLeads.length} leads atualizados para "${getStatusLabel(newStatus)}"`);
    } catch (error) {
      console.error('Erro ao atualizar leads em lote:', error);
      toast.error('Erro ao atualizar leads em lote');
    } finally {
      setIsUpdating(false);
    }
  };
  const handleExportCSV = () => {
    try {
      const csvContent = exportLeadsToCSV(filteredLeads);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `leads-cursos-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Arquivo CSV exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar arquivo CSV');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'interested': return 'bg-green-100 text-green-800';
      case 'enrolled': return 'bg-purple-100 text-purple-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'resolved': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Novo';
      case 'contacted': return 'Contatado';
      case 'interested': return 'Interessado';
      case 'enrolled': return 'Matriculado';
      case 'not_interested': return 'Não Interessado';
      case 'resolved': return 'Resolvido';
      default: return status;
    }
  };

  const uniqueCourses = Array.from(new Set(leads.map(lead => lead.selectedCourse).filter(Boolean)));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Gerenciar Leads de Cursos</h2>
          <p className="text-gray-600">Acompanhe e gerencie os interessados em nossos cursos</p>
        </div>
        <div className="flex space-x-3">
          {selectedLeads.length > 0 && (
            <button
              onClick={() => setShowBulkStatusModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Atualizar {selectedLeads.length} Selecionados
            </button>
          )}
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total de Leads</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <UserCheck className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Interessados</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.interested}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <BookOpen className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Matriculados</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.enrolled}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Taxa de Conversão</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.conversionRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Nome, email, telefone..."
                className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="new">Novos</option>
              <option value="contacted">Contatados</option>
              <option value="interested">Interessados</option>
              <option value="enrolled">Matriculados</option>
              <option value="not_interested">Não Interessados</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Curso</label>
            <select
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
            >
              <option value="all">Todos os cursos</option>
              {uniqueCourses.map(course => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCourseFilter('all');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {selectedLeads.length > 0 && (
          <div className="bg-purple-50 border-b border-purple-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-purple-800">
                {selectedLeads.length} lead(s) selecionado(s)
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowBulkStatusModal(true)}
                  className="text-sm font-medium text-purple-600 hover:text-purple-800"
                >
                  Alterar Status em Lote
                </button>
                <button
                  onClick={() => setSelectedLeads([])}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Limpar Seleção
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Curso de Interesse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                      <div className="text-sm text-gray-500">{lead.profession}</div>
                      <div className="text-sm text-gray-500">{lead.experience}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{lead.email}</div>
                    <div className="text-sm text-gray-500">{lead.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {lead.selectedCourseTitle || lead.selectedCourse || 'Interesse geral'}
                    </div>
                    {lead.interests.length > 0 && (
                      <div className="text-sm text-gray-500">
                        {lead.interests.slice(0, 2).join(', ')}
                        {lead.interests.length > 2 && ` +${lead.interests.length - 2}`}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                      {getStatusLabel(lead.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(lead.createdAt), "dd/MM/yyyy", { locale: ptBR })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={`mailto:${lead.email}`}
                        className="text-blue-600 hover:text-blue-900"
                        title="Enviar email"
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                      <a
                        href={`https://wa.me/55${lead.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-900"
                        title="WhatsApp"
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => setShowDeleteConfirm(lead.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowStatusModal(lead.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Alterar status"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowNotesModal(lead.id)}
                        className="text-orange-600 hover:text-orange-900"
                        title="Adicionar nota"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredLeads.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum lead encontrado com os filtros aplicados</p>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Status Update Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Atualizar Status em Lote</h3>
              <button
                onClick={() => setShowBulkStatusModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Alterar status de {selectedLeads.length} lead(s) selecionado(s):
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => handleBulkStatusUpdate('contacted')}
                disabled={isUpdating}
                className="p-3 border border-yellow-300 rounded-lg hover:bg-yellow-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-yellow-800">Contatado</div>
                <div className="text-sm text-yellow-600">Incrementa tentativas</div>
              </button>
              
              <button
                onClick={() => handleBulkStatusUpdate('interested')}
                disabled={isUpdating}
                className="p-3 border border-green-300 rounded-lg hover:bg-green-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-green-800">Interessado</div>
                <div className="text-sm text-green-600">Demonstrou interesse</div>
              </button>
              
              <button
                onClick={() => handleBulkStatusUpdate('enrolled')}
                disabled={isUpdating}
                className="p-3 border border-purple-300 rounded-lg hover:bg-purple-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-purple-800">Matriculado</div>
                <div className="text-sm text-purple-600">Conta para conversão</div>
              </button>
              
              <button
                onClick={() => handleBulkStatusUpdate('not_interested')}
                disabled={isUpdating}
                className="p-3 border border-red-300 rounded-lg hover:bg-red-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-red-800">Não Interessado</div>
                <div className="text-sm text-red-600">Sem interesse</div>
              </button>
              
              <button
                onClick={() => handleBulkStatusUpdate('resolved')}
                disabled={isUpdating}
                className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-gray-800">Resolvido</div>
                <div className="text-sm text-gray-600">Finalizado</div>
              </button>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkStatusModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                disabled={isUpdating}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Alterar Status</h3>
              <button
                onClick={() => setShowStatusModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'new')}
                disabled={isUpdating}
                className="p-3 border border-blue-300 rounded-lg hover:bg-blue-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-blue-800">Novo</div>
                <div className="text-sm text-blue-600">Lead recém-chegado</div>
              </button>
              
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'contacted')}
                disabled={isUpdating}
                className="p-3 border border-yellow-300 rounded-lg hover:bg-yellow-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-yellow-800">Contatado</div>
                <div className="text-sm text-yellow-600">Incrementa tentativas</div>
              </button>
              
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'interested')}
                disabled={isUpdating}
                className="p-3 border border-green-300 rounded-lg hover:bg-green-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-green-800">Interessado</div>
                <div className="text-sm text-green-600">Demonstrou interesse</div>
              </button>
              
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'enrolled')}
                disabled={isUpdating}
                className="p-3 border border-purple-300 rounded-lg hover:bg-purple-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-purple-800">Matriculado</div>
                <div className="text-sm text-purple-600">Conta para conversão</div>
              </button>
              
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'not_interested')}
                disabled={isUpdating}
                className="p-3 border border-red-300 rounded-lg hover:bg-red-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-red-800">Não Interessado</div>
                <div className="text-sm text-red-600">Sem interesse</div>
              </button>
              
              <button
                onClick={() => handleUpdateStatus(showStatusModal, 'resolved')}
                disabled={isUpdating}
                className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-left disabled:opacity-50"
              >
                <div className="font-medium text-gray-800">Resolvido</div>
                <div className="text-sm text-gray-600">Finalizado</div>
              </button>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                disabled={isUpdating}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Adicionar Nota</h3>
              <button
                onClick={() => setShowNotesModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Nota
              </label>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Digite sua observação sobre este lead..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNotesModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                disabled={isUpdating}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleAddNote(showNotesModal)}
                disabled={isUpdating || !newNote.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isUpdating ? 'Salvando...' : 'Salvar Nota'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteLead(showDeleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Detalhes do Lead</h3>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <p className="text-sm text-gray-900">{selectedLead.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{selectedLead.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefone</label>
                  <p className="text-sm text-gray-900">{selectedLead.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profissão</label>
                  <p className="text-sm text-gray-900">{selectedLead.profession}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Experiência</label>
                  <p className="text-sm text-gray-900">{selectedLead.experience}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedLead.status)}`}>
                    {getStatusLabel(selectedLead.status)}
                  </span>
                </div>
              </div>

              {selectedLead.selectedCourseTitle && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Curso Selecionado</label>
                  <p className="text-sm text-gray-900">{selectedLead.selectedCourseTitle}</p>
                </div>
              )}

              {selectedLead.interests.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Áreas de Interesse</label>
                  <p className="text-sm text-gray-900">{selectedLead.interests.join(', ')}</p>
                </div>
              )}

              {selectedLead.message && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mensagem</label>
                  <p className="text-sm text-gray-900">{selectedLead.message}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Data de Criação</label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(selectedLead.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tentativas de Contato</label>
                  <p className="text-sm text-gray-900">{selectedLead.contactAttempts || 0}</p>
                </div>
              </div>

              {selectedLead.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notas</label>
                  <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-900 whitespace-pre-wrap">
                    {selectedLead.notes}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center mt-6">
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowStatusModal(selectedLead.id)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Alterar Status
                </button>
                <button
                  onClick={() => setShowNotesModal(selectedLead.id)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Adicionar Nota
                </button>
              </div>
              <div className="flex space-x-3">
                <a
                  href={`mailto:${selectedLead.email}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Email
                </a>
                <a
                  href={`https://wa.me/55${selectedLead.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
